import React, { useEffect, useId, useRef, useState } from "react";
import { useSelector } from "react-redux";
import * as Sentry from "@sentry/react";

import AnokyeApiService from "@minohealth/common/services/AnokyeApiService";
import { DocumentSharingWindow, ReviewRequestWindow } from "../../components/anokye-sharing-window";
import AutoResizingTextarea from "@minohealth/common/components/AutoResizingTextarea";
import { PatientAutoComplete } from "@minohealth/common/components/patients-autocomplete";
import PreviewReport from "../../../chat-report-panel/PreviewReport";
import CommentingSection from "../commenting-section";
import ChatImageViewerBase from "../../../chat-session-info/ChatImageViewerBase";
import useDialogOpenState from "@minohealth/common/hooks/useDialogOpenState";
import dayjs from "dayjs";
import { fieldValueToMarkdown, markdownToFieldValue } from "../../../chat-report-panel/utils";
import useReviews from "./hooks/useReviews";
import useUserAccounts from "@minohealth/common/hooks/useUserAccounts";
import { MimeTypes } from "@minohealth/common/utils/files";
import { capitalise } from "@minohealth/common/utils/string-formatters";
import { useHistory } from "react-router-dom";
import { useChatImage } from "../../../../services/chat-provider/useChatImage";
import { DEFAULT_IMAGE_PREVIEW_SRC } from "../../../ChatImage";

import { IoChatboxEllipsesOutline, IoDocumentText, IoChatbubbleOutline } from "react-icons/io5";
import { MdEdit, MdOutlineDeleteForever, MdOutlineKeyboardBackspace } from "react-icons/md";
import { FaPlus,  FaSync } from "react-icons/fa";
import { showErrorMessage, showSuccessMessage, showWarningMessage } from "@minohealth/common/utils/notifiers";
import { IoIosClose, IoMdClose } from "react-icons/io";
import { AiOutlineFullscreen, AiOutlineLoading3Quarters } from "react-icons/ai";
import { BiComment } from "react-icons/bi";
import { IoMdShareAlt } from "react-icons/io";
import { BsThreeDots } from "react-icons/bs";
import LowCreditsDialog from "@/components/ui/LowCreditsDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ErrorCodes, parseAnokyeError, getUserFriendlyMessage } from "@/services/anokye-error-handler";

import "./style.css";

export default function ReportOverview() {
  const { user } = useSelector((state) => state.app.datastate);
  const [selectedReviewIndex, setSelectedReviewIndex] = useState(0);
  const [showEditReportPage, setShowEditReportPage] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [singleReport, setSingleReport] = useState();
  const [isDownloading, setIsDownloading] = useState(false);

  const imageThumbnailUrl = (() => {
    const image = singleReport?.images?.[0]
    if(image == null) return null
    if(image?.type === MimeTypes.PNG || image?.type === MimeTypes.JPEG) {
      return image?.thumbnailUrl ?? image?.resourceUrl
    }
    if(image?.type === MimeTypes.DICOM) {
      return image?.thumbnailUrl
    }

    throw new Error(`Unsupported image type "${image?.type}"`)
  })()
  const {
    previewUrl: loadedImagePreviewUrl,
    isLoading: isLoadingImagePreview,
    isError: isErrorImagePreview,
  } = useChatImage({ url: imageThumbnailUrl ?? undefined, userToken: user?.userToken })

  const imagePreviewUrl = (() => {
    if(isLoadingImagePreview) return DEFAULT_IMAGE_PREVIEW_SRC
    if(isErrorImagePreview) return DEFAULT_IMAGE_PREVIEW_SRC
    return loadedImagePreviewUrl
  })()

  const pathSegments = window.location.pathname.split("/");
  const reportId = pathSegments[pathSegments.length - 1];

  const history = useHistory();

  const { reportReviews, parsedReportReviews } = useReviews({ reportId, showEditReportPage });
  const previewImageDialogRef = useRef(null);
  const [shouldImageDialogOpen, setShouldImageDialogOpen] = useDialogOpenState(previewImageDialogRef);

  function previewImage() {
    setShouldImageDialogOpen(true);
  }

  function stopImagePreview() {
    setShouldImageDialogOpen(false);
  }

  useEffect(() => {
    async function getSingleReport() {
      try {
        const response = await AnokyeApiService(user.userToken).GetResources({
          resourceType: "reportById",
          reportId: reportId,
        });

        setSingleReport(response?.data);
      } catch (err) {
        const parsedError = parseAnokyeError(err);

        Sentry.captureException(err, {
          extra: {
            context: 'ReportOverview - getSingleReport',
            reportId: reportId || 'undefined',
            resourceType: 'reportById',
            userToken: user?.userToken ? 'present' : 'missing',
            userId: user?._id || 'undefined',
            errorCode: parsedError.code,
            errorMessage: parsedError.message
          }
        });

        const message = getUserFriendlyMessage(parsedError.code, "Failed to get Single report");
        showErrorMessage(message);
      }
    }

    getSingleReport();
  }, []);
  /** @type {React.RefObject<import("../anokye-content/components/anokye-sharing-window").SharingWindowExternalAPI<ChatReport>>} */
  const sharingWindowRef = useRef(null);
  const reviewWindowRef = useRef(null);

  function openSharingWindow() {
    sharingWindowRef.current?.openWindow({
      _id: parsedReportReviews[selectedReviewIndex]?.id,
      patientInfo: {
        name: parsedReportReviews[selectedReviewIndex]?.patient?.name,
        age: parsedReportReviews[selectedReviewIndex]?.patient?.age,
      },
      date: parsedReportReviews[selectedReviewIndex]?.date,
    });
  }

  function openReviewWindow() {
    reviewWindowRef.current?.openWindow({
      ...parsedReportReviews[selectedReviewIndex],
      _id: parsedReportReviews[selectedReviewIndex]?.id,
    });
  }

  function goToChat() {
    if (singleReport?.chatSessionId) {
      history.push(`/?id=${encodeURIComponent(singleReport.chatSessionId)}`);
    }
  }

  function validateReportFields(reportData) {
    const missingFields = [];
    const patient = reportData?.patient || reportData?.patientInfo;
    if (!patient?.name?.trim()) missingFields.push("Patient name");
    if (!patient?.id?.toString().trim()) missingFields.push("Patient ID");
    if (patient?.age == null) missingFields.push("Patient age");
    if (!patient?.gender) missingFields.push("Patient gender");
    const facility = reportData?.facility;
    if (!facility?.name?.trim()) missingFields.push("Facility name");
    if (!facility?.reportingDoctor?.trim()) missingFields.push("Reporting doctor");
    if (!facility?.reportDate?.trim()) missingFields.push("Report date");
    return { isValid: missingFields.length === 0, missingFields };
  }

  async function handleDownloadPDF() {
    if (isDownloading) return;
    const reportData = parsedReportReviews[selectedReviewIndex];
    if (!reportData) return;

    const validation = validateReportFields(reportData);
    if (!validation.isValid) {
      showWarningMessage(
        `Missing: ${validation.missingFields.join(", ")}.`,
        "Please fill in all required information before downloading"
      );
      setShowEditReportPage("owner");
      return;
    }

    setIsDownloading(true);
    try {
      const patient = reportData?.patient || reportData?.patientInfo;
      const patientName = patient?.name?.replace(/\s+/g, "_").toLowerCase() || "unknown_patient";
      const age = patient?.age || "unknown_age";
      const date = reportData?.facility?.reportDate
        ? new Date(reportData.facility.reportDate).toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0];
      const fileName = `medical_record_${patientName}_${age}_${date}.pdf`;

      const response = await fetch(new URL("pdf/generate-report", import.meta.env.VITE_SOCKET_URL), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reportData, blobImageUrl: null, fileName }),
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          context: "ReportOverview - handleDownloadPDF",
          reportId: reportId || "undefined",
          userId: user?._id || "undefined",
        },
      });
      showErrorMessage("Failed to download PDF. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  }

  return (
    <div className="report-overview-container">
      <div className="report-overview-header">
        <div 
          onClick={() => history.push("/anokye/report-history")}
          title="back"
          className="report-overview-back-btn">
          <MdOutlineKeyboardBackspace size={20} />
          <p>Back to Reports</p>
        </div>
        
        <div className="report-overview-header-content">
          <div className="report-overview-header-info">
            <h1 className="report-overview-title">
              {singleReport?.patientInfo?.name} - Medical Report
            </h1>
            <p className="report-overview-date">
              {singleReport?.date && dayjs(singleReport.date).format("dddd, MMMM D, YYYY")}
            </p>
          </div>
        </div>
      </div>

      <div className="report-overview-main">
        <div className={`report-overview-content ${showEditReportPage ? 'edit-mode' : ''}`}>
          {showEditReportPage === "owner" && (
            <OwnerEditReportWrapper
              setShowEditReportPage={setShowEditReportPage}
              originalReportData={reportReviews[selectedReviewIndex]?.files[0]?.data || singleReport}
              reportDataPreview={parsedReportReviews[selectedReviewIndex]}
            />
          )}
          {showEditReportPage === "review" && (
            <ReviewReportWrapper
              setShowEditReportPage={setShowEditReportPage}
              originalReportData={reportReviews[selectedReviewIndex]?.files[0]?.data || singleReport}
              reportDataPreview={parsedReportReviews[selectedReviewIndex]}
            />
          )}
          {!showEditReportPage && (
            <div className="report-overview-viewer">
              <div className="report-actions">
                <button
                  type="button"
                  className={`report-action-btn ${showComments ? 'active' : ''}`}
                  onClick={() => setShowComments(true)}>
                  <BiComment />
                  <span>Comment</span>
                </button>

                <button
                  type="button"
                  className="report-action-btn"
                  onClick={previewImage}
                  disabled={!singleReport?.images?.length}>
                  <AiOutlineFullscreen />
                  <span>Expand Image</span>
                </button>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button type="button" className="report-action-btn">
                      <BsThreeDots />
                      <span>More</span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {user?._id === singleReport?.owner && (
                      <DropdownMenuItem
                        disabled={!parsedReportReviews.length}
                        onSelect={() => setShowEditReportPage("owner")}>
                        <MdEdit />
                        <span>Edit Report</span>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      disabled={!parsedReportReviews.length}
                      onSelect={() => setShowEditReportPage("review")}>
                      <MdEdit />
                      <span>Review</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={!parsedReportReviews.length}
                      onSelect={openSharingWindow}>
                      <IoMdShareAlt />
                      <span>Share</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={!parsedReportReviews.length || !!reportReviews.length}
                      onSelect={openReviewWindow}>
                      <FaSync />
                      <span>Request Review</span>
                    </DropdownMenuItem>
                    {user?._id === singleReport?.owner && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          disabled={!parsedReportReviews.length || isDownloading}
                          onSelect={handleDownloadPDF}>
                          {isDownloading
                            ? <AiOutlineLoading3Quarters className="spin" />
                            : <IoDocumentText />}
                          <span>{isDownloading ? "Downloading..." : "Download PDF"}</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          disabled={!singleReport?.chatSessionId}
                          onSelect={goToChat}>
                          <IoChatbubbleOutline />
                          <span>Continue in Chat</span>
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {parsedReportReviews.length ? (
                <div className="report-preview-container">
                  <PreviewReport
                    blobImageUrl={imagePreviewUrl}
                    btnGroupModifiedClass="chat-report__btn-group-off"
                    pdfHeightClass="report-preview-pdf"
                    reportData={parsedReportReviews[selectedReviewIndex]}
                    reportId={parsedReportReviews[selectedReviewIndex]?.id || reportId}
                  />
               
                  
                  <ChatImageViewerBase
                    ref={previewImageDialogRef}
                    srcImage={singleReport?.images?.[0]}
                    bboxes={[]}
                    onCloseViewer={stopImagePreview}
                  />
                </div>
              ) : (
                <div className="report-loading">
                  <div className="report-loading-skeleton">
                    <div className="skeleton-pulse"></div>
                  </div>
                  <p>Loading report versions...</p>
                </div>
              )}
            </div>
          )}
        </div>


        <div className={`report-overview-sidebar ${showComments ? 'comments-open' : ''}`}>
          {!showComments ? (
            <section className="sidebar-section">
              <header className="sidebar-header">
                <h3 className="sidebar-title">Version History</h3>
              </header>

              <div className="sidebar-body">
                <div className="version-timeline">
                  <div className="timeline-track"></div>
                  
                  {reportReviews && reportReviews.length > 1 ? (
                    reportReviews.map((version, index) => {
                      if (index === reportReviews.length - 1) return null;
                      return (
                        <div
                          key={index}
                          onClick={() => setSelectedReviewIndex(index)}
                          className={`timeline-item ${selectedReviewIndex === index ? 'active' : ''}`}
                        >
                          <div className="timeline-node"></div>
                          <div className="timeline-content">
                            <div className="timeline-icon">
                              <IoChatboxEllipsesOutline />
                            </div>
                            <div className="timeline-details">
                              <p className="timeline-date">
                                {dayjs(version?.files[0]?.data?.date).format("MMMM D, h:mm A")}
                              </p>
                              <p className="timeline-author">{version?.files[0]?.reviewerDetails}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="timeline-empty">
                      <p>No revision history available</p>
                    </div>
                  )}
                </div>
              </div>
            </section>
          ) : (
            <section className="sidebar-section comments-section">
              <header className="sidebar-header">
                <h3 className="sidebar-title">Comments</h3>
                <button
                  type="button"
                  className="sidebar-close-btn"
                  onClick={() => setShowComments(false)}>
                  <IoMdClose size={20} />
                </button>
              </header>

              <div className="sidebar-body">
                <CommentingSection mobileOverrideClass="" reportId={reportId} />
              </div>
            </section>
          )}
        </div>
      </div>

      <DocumentSharingWindow ref={sharingWindowRef} />
      <ReviewRequestWindow ref={reviewWindowRef} />
    </div>
  );
}
/** @param {string} name */
function splitName(name) {
  const parts = name?.split(" ");
  if (parts.length === 1) return { firstName: parts[0], lastName: "" };
  return {
    firstName: parts.slice(0, -1).join(" "),
    lastName: parts[parts.length - 1],
  };
}

/**
 * @param {string} search
 * @param {string} value
 */
function isCaseInsensitiveSubstring(search = "", value = "") {
  return value.toLowerCase().includes(search.toLowerCase());
}

function EditReportPage({ originalReportData, reportDataPreview, setShowEditReportPage, isOwnerEdit = false, onSave, onDraft }) {
  const firstNameInputRef = useRef(null);
  const lastNameInputRef = useRef(null);
  const patientIdInputRef = useRef(null);
  const genderFieldsetRef = useRef(null);
  const ageInputRef = useRef(null);
  const formId = useId();

  const { user } = useSelector((state) => state.app.datastate);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [dynamicFields, setDynamicFields] = useState([]);

  /** @param {string} id */
  function getScopedId(id) {
    return `${formId}-${id}`;
  }

  useEffect(() => {
    if (reportDataPreview?.content) {
      const fields = reportDataPreview.content.map((field) => {
        try {
          return { ...field, value: fieldValueToMarkdown(field.value, null) };
        } catch (err) {
          Sentry.captureException(err, {
            extra: {
              context: 'ReportOverview - fieldValueToMarkdown conversion',
              reportId: reportDataPreview?.id || 'undefined',
              fieldLabel: field?.label || 'undefined',
              fieldValueType: typeof field?.value,
              userId: user?._id || 'undefined'
            }
          });
          return { ...field, value: null };
        }
      });
      setDynamicFields(fields);
    }
  }, [reportDataPreview?.content]);

  const { accounts } = useUserAccounts();
  const userFacility = accounts?.find((account) => account._id !== user._id && account.members != null);
  const defaultReportFacilityName = (() => {
    if (user?._id !== originalReportData?.owner) return null;
    return userFacility?.facilityName;
  })();

  function addField(label) {
    setDynamicFields((fields) => {
      return [...fields, { label: capitalise(label), value: "" }];
    });
  }

  function removeField(label) {
    setDynamicFields((fields) => {
      return fields.filter((field) => field.label !== label);
    });
  }

  /** @param {React.FormEvent<HTMLFormElement>} ev */
  function handleAddField(ev) {
    ev.preventDefault();
    const formData = new FormData(ev.target);
    addField(formData.get("field"));
    ev.target.reset();
  }

  async function saveDraft() {
    if (!onDraft || isSavingDraft) return;
    setIsSavingDraft(true);

    const form = document.querySelector(".edit-report-form");
    const formData = new FormData(form);
    const { newReportData, reportContent } = buildPayload(formData);
    if (reportContent == null) { setIsSavingDraft(false); return; }

    try {
      await onDraft(newReportData, reportContent);
      setShowEditReportPage(false);
    } catch {
      // onDraft handles error messaging
    } finally {
      setIsSavingDraft(false);
    }
  }

  function buildPayload(formData) {
    const newReportData = {
      patient: {
        name: [formData.get("patient-first-name"), formData.get("patient-last-name")]
          .join(" ").split(" ").map(capitalise).join(" "),
        id: formData.get("patient-id")?.toUpperCase(),
        age: Number(formData.get("patient-age")),
        gender: formData.get("patient-gender"),
      },
      facility: {
        name: formData.get("facility-name").split(" ").map(capitalise).join(" "),
        reportingDoctor: formData.get("facility-reporting-doctor").split(" ").map(capitalise).join(" "),
        reportDate: formData.get("report-date"),
      },
      date: new Date(formData.get("report-date")).toISOString(),
      content: [],
    };

    const DEDICATED_FIELDS = new Set(["modality", "technique", "examination-reason", "clinical-history"]);
    for (let [key, value] of formData.entries()) {
      if (key.startsWith("patient-") || key.startsWith("facility-") || key.startsWith("report-") || DEDICATED_FIELDS.has(key)) continue;
      newReportData.content.push({ label: key, value: capitalise(value) });
    }

    let reportContent;
    try {
      reportContent = JSON.stringify(
        Object.fromEntries(newReportData.content.map((f) => [f.label, markdownToFieldValue(f.value)])),
        null, 2
      );
    } catch (err) {
      Sentry.captureException(err, {
        extra: { context: "EditReportPage - buildPayload", reportId: reportDataPreview?.id || "undefined" },
      });
      showErrorMessage("Invalid markdown value format");
      reportContent = null;
    }

    return { newReportData, reportContent };
  }

  /** @param {React.FormEvent<HTMLFormElement>} ev */
  async function handleSubmit(ev) {
    ev.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    const { newReportData, reportContent } = buildPayload(new FormData(ev.target));
    if (reportContent == null) { setIsSubmitting(false); return; }

    try {
      await onSave(newReportData, reportContent);
      setShowEditReportPage(false);
    } catch {
      // onSave handles error messaging
    } finally {
      setIsSubmitting(false);
    }
  }

  /** @param {*} patient */
  function autoFillPatientInfo(patient) {
    if (patient == null) return;

    let { firstName, lastName } = splitName(patient.patientName);

    if (firstNameInputRef.current != null) firstNameInputRef.current.value = firstName;
    if (lastNameInputRef.current != null) lastNameInputRef.current.value = lastName;
    if (patientIdInputRef.current != null) patientIdInputRef.current.value = patient.patientId;
    if (genderFieldsetRef.current != null) {
      for (let radio of genderFieldsetRef.current.querySelectorAll("input[type=radio]")) {
        radio.checked = radio.value === patient.gender;
      }
    }
    if (ageInputRef.current != null) ageInputRef.current.value = patient?.age;
  }

  return (
    <div className="edit-report-page">
      <header className="edit-report-page-header">
        <h2>Edit medical Report</h2>
        <IoIosClose className="close-icon" onClick={() => setShowEditReportPage(false)} size={40} />
      </header>

      <form id={formId} className="edit-report-form chat-report__form" onSubmit={handleSubmit}>
        <div className="chat-report__form-fields">
          <fieldset data-transparent>
            <legend className="sr-only">Patient information</legend>

            <div className="form-inline-fieldset">
              <div className="form-field">
                <label className="form-field__label" htmlFor={getScopedId("patient-first-name")}>
                  First Name
                </label>
                <PatientAutoComplete
                  className="patient-autocomplete form-field__combobox-wrapper"
                  searchKey="patientName"
                  onSelectPatient={autoFillPatientInfo}
                  filterFn={(search, patient) => {
                    let { firstName = "" } = splitName(patient.patientName);
                    return isCaseInsensitiveSubstring(search, firstName);
                  }}>
                  <PatientAutoComplete.Input
                    type="text"
                    disabled={!isOwnerEdit}
                    className="form-field__input form-field__combobox-input"
                    name="patient-first-name"
                    id={getScopedId("patient-first-name")}
                    defaultValue={splitName(reportDataPreview?.patient?.name ?? "").firstName}
                    required
                    ref={firstNameInputRef}
                  />
                  <PatientAutoComplete.List className="form-field__combobox-list" />
                </PatientAutoComplete>
              </div>

              <div className="form-field">
                <label className="form-field__label" htmlFor={getScopedId("patient-last-name")}>
                  Last Name
                </label>
                <PatientAutoComplete
                  className="patient-autocomplete form-field__combobox-wrapper"
                  searchKey="patientName"
                  onSelectPatient={autoFillPatientInfo}
                  filterFn={(search, patient) => {
                    let { lastName = "" } = splitName(patient.patientName);
                    return isCaseInsensitiveSubstring(search, lastName);
                  }}>
                  <PatientAutoComplete.Input
                    type="text"
                    disabled={!isOwnerEdit}
                    className="form-field__input form-field__combobox-input"
                    name="patient-last-name"
                    id={getScopedId("patient-last-name")}
                    defaultValue={splitName(reportDataPreview?.patient?.name ?? "").lastName}
                    required
                    ref={lastNameInputRef}
                  />
                  <PatientAutoComplete.List className="form-field__combobox-list" />
                </PatientAutoComplete>
              </div>
            </div>

            <div className="form-inline-fieldset">
              <div className="form-field">
                <label className="form-field__label" htmlFor={getScopedId("patient-age")}>
                  Age
                </label>
                <input
                  type="number"
                  disabled={!isOwnerEdit}
                  className="form-field__input"
                  name="patient-age"
                  id={getScopedId("patient-age")}
                  defaultValue={reportDataPreview?.patient?.age}
                  min={0}
                  required
                />
              </div>
              <div className="form-field">
                <label className="form-field__label" htmlFor={getScopedId("patient-id")}>
                  Patient ID
                </label>
                <PatientAutoComplete
                  className="patient-autocomplete form-field__combobox-wrapper"
                  searchKey="patientId"
                  onSelectPatient={autoFillPatientInfo}>
                  <PatientAutoComplete.Input
                    type="text"
                    disabled={!isOwnerEdit}
                    className="form-field__input form-field__combobox-input"
                    name="patient-id"
                    id={getScopedId("patient-id")}
                    defaultValue={reportDataPreview?.patient?.id}
                    required
                    ref={patientIdInputRef}
                  />
                  <PatientAutoComplete.List className="form-field__combobox-list" />
                </PatientAutoComplete>
              </div>
            </div>

            <div className="form-inline-fieldset">
              <div className="form-field">
                <label className="form-field__label" htmlFor="facility-name">
                  Facility Name
                </label>
                <input
                  type="text"
                  className="form-field__input"
                  id="facility-name"
                  name="facility-name"
                  defaultValue={reportDataPreview?.facility?.name ?? defaultReportFacilityName ?? ""}
                />
              </div>
              <div className="form-field">
                <label className="form-field__label" htmlFor="facility-reporting-doctor">
                  Reporting Doctor
                </label>
                <input
                  type="text"
                  className="form-field__input"
                  id="facility-reporting-doctor"
                  name="facility-reporting-doctor"
                  defaultValue={reportDataPreview?.facility?.reportingDoctor ?? ""}
                />
              </div>
            </div>

            <div className="form-field">
              <label className="form-field__label" htmlFor="facility-report-date">
                Report Date
              </label>
              <input
                type="date"
                required
                disabled={!isOwnerEdit}
                className="form-field__input"
                id="report-date"
                name="report-date"
                defaultValue={reportDataPreview?.date?.split("T")[0]}
              />
            </div>

            <fieldset className="form-field" ref={genderFieldsetRef}>
              <legend className="form-field__label">Gender (Sex)</legend>

              <div className="form-inline-radio-fieldset">
                <div className="form-radio-field">
                  <input
                    type="radio"
                    required
                    disabled={!isOwnerEdit}
                    className="form-field__input"
                    name="patient-gender"
                    id={getScopedId("patient-gender-male")}
                    value="Male"
                    defaultChecked={reportDataPreview?.patient?.gender === "Male"}
                  />
                  <label htmlFor={getScopedId("patient-gender-male")}>Male</label>
                </div>
                <div className="form-radio-field">
                  <input
                    type="radio"
                    required
                    disabled={!isOwnerEdit}
                    className="form-field__input"
                    name="patient-gender"
                    id={getScopedId("patient-gender-female")}
                    value="Female"
                    defaultChecked={reportDataPreview?.patient?.gender === "Female"}
                  />
                  <label htmlFor={getScopedId("patient-gender-female")}>Female</label>
                </div>
              </div>
            </fieldset>
          </fieldset>

          {reportDataPreview?.modality != null && (
            <div className="form-field">
              <label className="form-field__label" data-variant="huge" htmlFor={getScopedId("modality")}>
                Modality
              </label>

              <AutoResizingTextarea
                className="form-field__input"
                name={"modality"}
                id={getScopedId("modality")}
                defaultValue={reportDataPreview?.modality}
                required
                disabled
              />
            </div>
          )}

          {reportDataPreview?.technique != null && (
            <div className="form-field">
              <label className="form-field__label" data-variant="huge" htmlFor={getScopedId("technique")}>
                Technique
              </label>

              <AutoResizingTextarea
                className="form-field__input"
                name={"technique"}
                id={getScopedId("technique")}
                defaultValue={reportDataPreview?.technique}
                required
                disabled
              />
            </div>
          )}

          {reportDataPreview?.examinationReason != null && (
            <div className="form-field">
              <label
                className="form-field__label"
                data-variant="huge"
                htmlFor={getScopedId("examination-reason")}>
                Reason for examination
              </label>

              <AutoResizingTextarea
                className="form-field__input"
                name={"examination-reason"}
                id={getScopedId("examination-reason")}
                defaultValue={reportDataPreview?.examinationReason}
                required
                disabled
              />
            </div>
          )}

          {reportDataPreview?.clinicalHistory != null && (
            <div className="form-field">
              <label
                className="form-field__label"
                data-variant="huge"
                htmlFor={getScopedId("clinical-history")}>
                Relevant clinical history
              </label>

              <AutoResizingTextarea
                className="form-field__input"
                name={"clinical-history"}
                id={getScopedId("clinical-history")}
                defaultValue={reportDataPreview?.clinicalHistory}
                required
                disabled
              />
            </div>
          )}

          {dynamicFields.map((field) => (
            <div className="form-field" key={field.label}>
              <div className="form-field__header">
                <label
                  className="form-field__label"
                  data-variant="huge"
                  htmlFor={getScopedId(field.label.toLowerCase().replace(/\s/, "-"))}>
                  {field.label}
                </label>

                {!field.required && (
                  <button
                    type="button"
                    className="form-field__remove-btn"
                    onClick={() => removeField(field.label)}>
                    <MdOutlineDeleteForever />
                    <span>Remove</span>
                  </button>
                )}
              </div>
              <AutoResizingTextarea
                className="form-field__input"
                name={field.label}
                id={getScopedId(field.label.toLowerCase().replace(/\s/, "-"))}
                defaultValue={field.value}
                required={field.required}
              />
            </div>
          ))}

          <div className="add-field-form-body">
            <input name="field" form="add-field-form" required />
            <button
              type="submit"
              form="add-field-form"
              className="chat-report__btn chat-report__add-field-btn">
              <FaPlus />
              <span>Add Field</span>
            </button>
          </div>
        </div>

        <div className="chat-report__btn-group chat-report__edit-form-btn-group">
          <button
            type="button"
            className="chat-report__btn"
            data-variant="secondary"
            onClick={() => setShowEditReportPage(false)}
            disabled={isSubmitting}>
            Cancel
          </button>
          {onDraft && (
            <button
              type="button"
              className="chat-report__btn"
              data-variant="secondary"
              onClick={saveDraft}
              disabled={isSubmitting}>
              {isSavingDraft && <AiOutlineLoading3Quarters className="spin" style={{ "--duration": "5s" }} />}
              <span>Save Draft</span>
            </button>
          )}
          <button type="submit" className="chat-report__btn" data-variant="primary" disabled={isSubmitting}>
            {isSubmitting && <AiOutlineLoading3Quarters className="spin" style={{ "--duration": "5s" }} />}
            <span>{isOwnerEdit ? "Save changes" : "Complete"}</span>
          </button>
        </div>
      </form>

      <form id="add-field-form" onSubmit={handleAddField}></form>
    </div>
  );
}

function OwnerEditReportWrapper({ originalReportData, reportDataPreview, setShowEditReportPage }) {
  const { user } = useSelector((state) => state.app.datastate);

  async function handleSave(newReportData, reportContent) {
    const response = await fetch(
      new URL(`chat/reports/${reportDataPreview.id}`, import.meta.env.VITE_SOCKET_URL).href,
      {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${user.userToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          current: reportContent,
          patientInfo: newReportData.patient,
          facilityInfo: newReportData.facility,
          date: newReportData.date,
        }),
      }
    );
    if (!response.ok) {
      Sentry.captureException(new Error(`updateReport HTTP ${response.status}`), {
        extra: { context: "OwnerEditReportWrapper - handleSave", reportId: reportDataPreview?.id, userId: user?._id },
      });
      showErrorMessage("Failed to update report");
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    showSuccessMessage("Report updated successfully");
  }

  return (
    <EditReportPage
      isOwnerEdit={true}
      originalReportData={originalReportData}
      reportDataPreview={reportDataPreview}
      setShowEditReportPage={setShowEditReportPage}
      onSave={handleSave}
    />
  );
}

function ReviewReportWrapper({ originalReportData, reportDataPreview, setShowEditReportPage }) {
  const { user } = useSelector((state) => state.app.datastate);
  const [showLowCreditsDialog, setShowLowCreditsDialog] = useState(false);
  const [lowCreditsMessage, setLowCreditsMessage] = useState("");

  async function handleSave(_newReportData, reportContent) {
    try {
      const { status } = await AnokyeApiService(user.userToken).PostReview({
        reviewType: "newReview",
        reviewText: { ...(originalReportData ?? {}), current: reportContent },
        reviewerId: user?._id,
        reportId: reportDataPreview.id,
      });
      if (!status.toString().match(/^2.*/)) throw new Error("Unexpected status");
    } catch (error) {
      const parsedError = parseAnokyeError(error);
      if (parsedError.code === ErrorCodes.INSUFFICIENT_FUNDS) {
        setLowCreditsMessage(parsedError.message || parsedError.details?.message || "You do not have enough credits to submit a review.");
        setShowLowCreditsDialog(true);
        throw error;
      }
      Sentry.captureException(error, {
        extra: { context: "ReviewReportWrapper - handleSave", reportId: reportDataPreview?.id, userId: user?._id, errorCode: parsedError.code },
      });
      showErrorMessage(getUserFriendlyMessage(parsedError.code, "Failed to submit review"));
      throw error;
    }
  }

  async function handleDraft(_newReportData, draftContent) {
    try {
      const { status } = await AnokyeApiService(user.userToken).SaveDraft({
        draft: draftContent,
        reportId: reportDataPreview.id,
      });
      if (!status.toString().match(/^2.*/)) throw new Error("Unexpected status");
      showSuccessMessage("Draft saved successfully");
    } catch (error) {
      const parsedError = parseAnokyeError(error);
      if (parsedError.code === ErrorCodes.INSUFFICIENT_FUNDS) {
        setLowCreditsMessage(parsedError.message || parsedError.details?.message || "You do not have enough credits to save a draft.");
        setShowLowCreditsDialog(true);
        throw error;
      }
      Sentry.captureException(error, {
        extra: { context: "ReviewReportWrapper - handleDraft", reportId: reportDataPreview?.id, userId: user?._id, errorCode: parsedError.code },
      });
      showErrorMessage(getUserFriendlyMessage(parsedError.code, "Failed to save draft"));
      throw error;
    }
  }

  return (
    <>
      <EditReportPage
        isOwnerEdit={false}
        originalReportData={originalReportData}
        reportDataPreview={reportDataPreview}
        setShowEditReportPage={setShowEditReportPage}
        onSave={handleSave}
        onDraft={handleDraft}
      />
      <LowCreditsDialog
        open={showLowCreditsDialog}
        onOpenChange={setShowLowCreditsDialog}
        title="Low Credits"
        description={lowCreditsMessage || "You do not have enough credits."}
        buttonText="Top up"
        onButtonClick={() => setShowLowCreditsDialog(false)}
      />
    </>
  );
}
